import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  MapPin,
  FileText,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Archive,
  Mail,
  Upload,
  Lightbulb,
  Link2,
  Flag,
  Zap,
  Check,
  X,
  Pin,
  PinOff,
  Star,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  ShieldCheck,
  User,
  RefreshCw,
  Loader2,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { BrainDumpCard } from '../components/BrainDumpCard';

const STATUS_CLASSES = {
  active: 'badge-active',
  planned: 'badge-planned',
  inactive: 'badge-inactive',
};

const STATUS_LABELS = {
  active: 'Actief',
  planned: 'Gepland',
  inactive: 'Inactief',
};

export default function Dashboard() {
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [insightsNewCount, setInsightsNewCount] = useState(0);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [scoresMap, setScoresMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [allLocsExpanded, setAllLocsExpanded] = useState(false);
  // Pull-to-refresh
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const [bulkApproving, setBulkApproving] = useState(false);
  const pulling = useRef(false);
  const containerRef = useRef(null);
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();

  const fetchInsights = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/ai/insights?status=new&limit=5');
      setInsights(res.data.insights || []);
      setInsightsNewCount(res.data.new_count || 0);
    } catch (e) { /* silent */ }
  };

  const refreshAll = useCallback(async () => {
    try {
      const api = authAxios();
      const [locRes, statsRes, actionsRes, meRes] = await Promise.all([
        api.get('/locations'),
        api.get('/entries/stats'),
        api.get('/dashboard/actions'),
        api.get('/auth/me'),
      ]);
      setLocations(locRes.data);
      setStats(statsRes.data);
      setActions(actionsRes.data.actions || []);
      setPinnedIds(meRes.data.pinned_locations || []);
      try {
        const scoresRes = await api.get('/compliance-scores');
        const map = {};
        (scoresRes.data.scores || []).forEach(s => { map[s.location_id] = s; });
        setScoresMap(map);
      } catch (e) { /* scores optional */ }
    } catch (e) {
      console.error(e);
    }
  }, [authAxios]);

  useEffect(() => {
    const load = async () => {
      await refreshAll();
      setLoading(false);
    };
    load();
    fetchInsights();
  }, [refreshAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull-to-refresh touch handlers (mobile only, < 768px)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (window.innerWidth >= 768 || refreshing) return;
      // Only activate when scrolled to top
      const scrollParent = el.closest('.page-fade-in') || el.closest('[class*="overflow"]') || document.scrollingElement;
      if (scrollParent && scrollParent.scrollTop > 5) return;
      if (window.scrollY > 5) return;
      touchStartY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onTouchMove = (e) => {
      if (!pulling.current || refreshing) return;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (dy > 0) {
        setPullDistance(Math.min(dy * 0.5, 80));
      } else {
        pulling.current = false;
        setPullDistance(0);
      }
    };

    const onTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistance >= 60) {
        setRefreshing(true);
        setPullDistance(50); // Hold at indicator position
        await refreshAll();
        await fetchInsights();
        setRefreshing(false);
      }
      setPullDistance(0);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullDistance, refreshing, refreshAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePin = useCallback(async (locationId) => {
    const newPinned = pinnedIds.includes(locationId)
      ? pinnedIds.filter(id => id !== locationId)
      : [...pinnedIds, locationId];
    setPinnedIds(newPinned);
    try {
      const api = authAxios();
      await api.put('/users/me/pinned-locations', { pinned_locations: newPinned });
      toast.success(newPinned.includes(locationId) ? 'Locatie vastgezet' : 'Locatie losgemaakt');
    } catch (e) {
      setPinnedIds(pinnedIds); // revert
      toast.error('Fout bij bijwerken');
    }
  }, [authAxios, pinnedIds]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6 animate-pulse">
          <div className="h-5 w-32 bg-slate-100 dark:bg-[#21262D] rounded mb-4" />
          <div className="space-y-3">
            <div className="h-10 bg-slate-100 dark:bg-[#21262D] rounded" />
            <div className="h-10 bg-slate-100 dark:bg-[#21262D] rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 animate-pulse">
              <div className="h-4 w-24 bg-slate-100 dark:bg-[#21262D] rounded mb-3" />
              <div className="h-3 w-16 bg-slate-100 dark:bg-[#21262D] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeLocations = locations.filter(l => l.status === 'active').length;
  const pinnedLocations = locations.filter(l => pinnedIds.includes(l.location_id));
  const otherLocations = locations.filter(l => !pinnedIds.includes(l.location_id));
  const hasPinned = pinnedLocations.length > 0;

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      const api = authAxios();
      const res = await api.post('/entries/bulk-approve');
      const { approved, manual_review, total } = res.data;
      toast.success(`${approved} van ${total} items goedgekeurd${manual_review > 0 ? ` (${manual_review} items vereisen handmatige review)` : ''}`);
      refreshAll();
    } catch (e) {
      toast.error('Fout bij goedkeuren');
    } finally {
      setBulkApproving(false);
    }
  };

  // Compliance items for Vandaag
  const complianceItemsCount = actions.filter(a => a.type.startsWith('compliance')).length;
  const overdueCount = actions.filter(a => a.type === 'compliance_overdue').length;
  const pendingEntries = actions.filter(a => a.type === 'entry_pending');

  // Location map for names
  const locationMap = {};
  locations.forEach(l => { locationMap[l.location_id] = l.name; });

  return (
    <div ref={containerRef} className="space-y-6 max-w-[1400px] relative">
      {/* Pull-to-refresh indicator (mobile only) */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="flex items-center justify-center md:hidden"
          style={{
            height: refreshing ? 40 : pullDistance * 0.6,
            transition: refreshing ? 'none' : 'height 0.1s ease-out',
            overflow: 'hidden',
          }}
          data-testid="pull-to-refresh-indicator"
        >
          <RefreshCw
            className={`w-5 h-5 text-[#2E75B6] transition-transform duration-200 ${refreshing ? 'animate-spin' : ''}`}
            style={{ opacity: Math.min(1, pullDistance / 60), transform: `rotate(${pullDistance * 4}deg)` }}
          />
        </div>
      )}

      {/* SNEL VASTLEGGEN — brain-dump capture */}
      <BrainDumpCard onSaved={refreshAll} />

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-kpi-strip">
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 shadow-sm border-t-[3px] border-t-[#2E75B6]">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-[#2E75B6]" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-[#8B949E]">Actieve Locaties</span>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900 dark:text-[#E6EDF3] tabular-nums">{activeLocations}</div>
        </div>

        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 shadow-sm border-t-[3px] border-t-[#7c3aed]">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-[#7c3aed]" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-[#8B949E]">Vault Entries</span>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900 dark:text-[#E6EDF3] tabular-nums">{stats?.active || (stats?.total - (stats?.archived || 0)) || 0}</div>
        </div>

        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 shadow-sm border-t-[3px] border-t-[#ea580c]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-[#ea580c]" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-[#8B949E]">Compliance Items</span>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900 dark:text-[#E6EDF3] tabular-nums">{complianceItemsCount}</div>
        </div>

        <div className={`bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 shadow-sm border-t-[3px] border-t-[#dc2626] transition-all duration-300 ${overdueCount > 0 ? 'ring-2 ring-red-500/20 shadow-red-500/10' : ''}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#dc2626]" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-[#8B949E]">Te Laat</span>
          </div>
          <div className="text-2xl font-bold font-heading text-red-600 dark:text-red-400 tabular-nums">{overdueCount}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* SECTION A: Vandaag */}
      <VandaagSection
        actions={actions}
        locationMap={locationMap}
        navigate={navigate}
        handleBulkApprove={handleBulkApprove}
        bulkApproving={bulkApproving}
        authAxios={authAxios}
        refreshAll={refreshAll}
      />

      {/* Stats Summary Bar */}
      <div className="bg-slate-50 dark:bg-[#1C2128] rounded-xl border border-slate-200/50 dark:border-[#30363D] p-3 sm:px-4 flex flex-wrap items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#2E75B6]" />
          <span className="text-xs text-slate-600 dark:text-[#8B949E]">
            <strong className="text-slate-900 dark:text-[#E6EDF3]">{stats?.total || 0}</strong> items
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-orange-500" />
          <span className="text-xs text-slate-600 dark:text-[#8B949E]">
            <strong className="text-orange-600 dark:text-orange-400">{stats?.pending || 0}</strong> in behandeling
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-slate-600 dark:text-[#8B949E]">
            <strong className="text-green-600 dark:text-green-400">{activeLocations}</strong> actieve locaties
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-slate-400" />
          <span className="text-xs text-slate-600 dark:text-[#8B949E]">
            <strong className="text-slate-700 dark:text-[#C9D1D9]">{stats?.archived || 0}</strong> gearchiveerd
          </span>
        </div>
      </div>
          {/* SECTION B: Mijn locaties (pinned) */}
          {hasPinned && (
            <div data-testid="dashboard-pinned-locations">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <h3 className="font-heading text-sm uppercase tracking-wide text-slate-600 dark:text-[#8B949E]">Mijn locaties</h3>
                </div>
                <span className="text-xs text-slate-400 dark:text-[#484F58]">{pinnedLocations.length} vastgezet</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                {pinnedLocations.map(loc => (
                  <LocationCard
                    key={loc.location_id}
                    loc={loc}
                    stats={stats}
                    isPinned
                    onTogglePin={togglePin}
                    onNavigate={() => navigate(`/locatie/${loc.location_id}`)}
                    score={scoresMap[loc.location_id]}
                    actions={actions}
                    large
                  />
                ))}
              </div>
            </div>
          )}

          {/* SECTION C: Alle locaties */}
          <div data-testid="dashboard-all-locations">
            {hasPinned ? (
              <button
                onClick={() => setAllLocsExpanded(!allLocsExpanded)}
                className="flex items-center justify-between w-full mb-3 group"
                data-testid="dashboard-toggle-all-locations"
              >
                <h3 className="font-heading text-sm uppercase tracking-wide text-slate-600 dark:text-[#8B949E] group-hover:text-slate-800 dark:group-hover:text-[#C9D1D9] transition-colors">
                  Alle locaties ({otherLocations.length})
                </h3>
                {allLocsExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
            ) : (
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm uppercase tracking-wide text-slate-600 dark:text-[#8B949E]">Locaties</h3>
                <span className="text-xs text-slate-400 dark:text-[#484F58]">{locations.length} locaties</span>
              </div>
            )}

            {(!hasPinned || allLocsExpanded) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3" data-testid="dashboard-locations-grid">
                {otherLocations.map(loc => (
                  <LocationCard
                    key={loc.location_id}
                    loc={loc}
                    stats={stats}
                    isPinned={false}
                    onTogglePin={togglePin}
                    onNavigate={() => navigate(`/locatie/${loc.location_id}`)}
                    score={scoresMap[loc.location_id]}
                    actions={actions}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {/* Pending Queue */}
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="dashboard-pending-queue">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">In Behandeling</h3>
              <span className="text-xs px-2 py-0.5 rounded-full badge-pending font-medium tabular-nums">
                {pendingEntries.length}
              </span>
            </div>

            {pendingEntries.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-[#484F58] text-center py-6">
                Geen items in behandeling
              </p>
            ) : (
              <div className="space-y-2">
                {pendingEntries.slice(0, 10).map(entry => (
                  <button
                    key={entry.entry_id}
                    onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
                    className="w-full text-left p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors duration-200 group"
                  >
                    <p className="text-xs font-medium text-slate-900 dark:text-[#E6EDF3] truncate group-hover:text-[#2E75B6]">{entry.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400 dark:text-[#484F58]">{locationMap[entry.location_id] || entry.location_id}</span>
                      {entry.source === 'email' && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600">
                          <Mail className="w-2.5 h-2.5" /> Email
                        </span>
                      )}
                      {entry.source === 'document_upload' && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600">
                          <FileText className="w-2.5 h-2.5" /> Document
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400 dark:text-[#484F58]">
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : ''}
                      </span>
                    </div>
                  </button>
                ))}
                {pendingEntries.length > 10 && (
                  <button
                    onClick={() => navigate('/kennisbank?status=pending')}
                    className="w-full text-center text-xs text-[#2E75B6] hover:underline py-1"
                  >
                    Alle {pendingEntries.length} bekijken
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Locatie Compliance Health Panel */}
          <LocatieCompliancePanel scoresMap={scoresMap} locations={locations} navigate={navigate} />

          {/* Vertrouwelijk overzicht (owner-only) */}
          {user?.role === 'owner' && <VertrouwelijkOverzicht authAxios={authAxios} navigate={navigate} />}

          {/* AI Insights Widget */}
          {insights.length > 0 && (
            <InsightsWidget
              insights={insights}
              newCount={insightsNewCount}
              onUpdate={async (id, status) => {
                try {
                  const api = authAxios();
                  await api.put(`/ai/insights/${id}`, { status });
                  toast.success(status === 'accepted' ? 'Inzicht geaccepteerd' : 'Inzicht afgewezen');
                  fetchInsights();
                } catch (e) { toast.error('Bijwerken mislukt'); }
              }}
              onViewAll={() => navigate('/inzichten')}
            />
          )}
        </div>
      </div>
    </div>
  );
}


// ---- Vandaag Section ----

function VandaagSection({ actions, locationMap, navigate, handleBulkApprove, bulkApproving, authAxios, refreshAll }) {
  if (!actions || actions.length === 0) {
    return (
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-green-200 dark:border-green-500/30 p-8 text-center" data-testid="vandaag-all-clear">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="font-heading text-lg font-bold text-green-700 dark:text-green-400 mb-1">Alles afgehandeld ✓</h2>
        <p className="text-sm text-green-600/70 dark:text-green-400/60">Geen openstaande acties. Goed bezig!</p>
      </div>
    );
  }

  const pendingCount = actions.filter(a => a.type === 'entry_pending').length;

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden" data-testid="vandaag-section">
      <div className="px-4 py-3 bg-slate-50 dark:bg-[#1C2128] border-b border-slate-200/80 dark:border-[#30363D] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base font-semibold text-slate-900 dark:text-[#E6EDF3]">Vandaag</h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-[#30363D] text-slate-700 dark:text-[#C9D1D9] font-medium tabular-nums">
            {actions.length} acties
          </span>
        </div>
        {pendingCount > 0 && (
          <button
            data-testid="bulk-approve-dashboard"
            onClick={handleBulkApprove}
            disabled={bulkApproving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-[11px] font-medium hover:bg-[#27649C] disabled:opacity-50 transition-colors"
          >
            {bulkApproving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Alles goedkeuren
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
        {actions.map(action => (
          <VandaagActionItem 
            key={action.action_id} 
            action={action} 
            locationMap={locationMap} 
            navigate={navigate} 
            authAxios={authAxios}
            refreshAll={refreshAll}
          />
        ))}
      </div>
    </div>
  );
}

function VandaagActionItem({ action, locationMap, navigate, authAxios, refreshAll }) {
  const [completing, setCompleting] = useState(false);

  const handleCompleteCompliance = async (e) => {
    e.stopPropagation();
    if (completing) return;
    setCompleting(true);
    try {
      const api = authAxios();
      await api.post(`/compliance-items/${action.target_id}/complete`, { notes: 'Afgerond vanaf dashboard' });
      toast.success('Taak afgerond');
      refreshAll();
    } catch (err) {
      toast.error('Fout bij afronden');
      setCompleting(false);
    }
  };

  const handleApproveEntry = async (e) => {
    e.stopPropagation();
    if (completing) return;
    setCompleting(true);
    try {
      const api = authAxios();
      await api.put(`/entries/${action.target_id}`, { entry_status: 'active' });
      toast.success('Document goedgekeurd');
      refreshAll();
    } catch (err) {
      toast.error('Fout bij goedkeuren');
      setCompleting(false);
    }
  };

  let borderColor = '';
  let TypeIcon = FileText;
  let typeLabel = '';
  
  if (action.type === 'compliance_overdue') {
    borderColor = 'border-l-red-500'; TypeIcon = AlertTriangle; typeLabel = 'Compliance';
  } else if (action.type === 'compliance_due_soon') {
    borderColor = 'border-l-amber-500'; TypeIcon = Clock; typeLabel = 'Compliance';
  } else if (action.type === 'entry_pending') {
    borderColor = 'border-l-[#2E75B6]'; TypeIcon = FileText; typeLabel = 'Beoordelen';
  } else if (action.type === 'contract_event') {
    borderColor = 'border-l-purple-500'; TypeIcon = Link2; typeLabel = 'Contract';
  }

  const navigateTo = () => {
    if (action.type.startsWith('compliance')) {
      navigate('/compliance');
    } else {
      navigate(`/kennisbank/${action.target_id}`);
    }
  };

  return (
    <button
      onClick={navigateTo}
      className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors group flex items-center justify-between gap-4 border-l-4 ${borderColor}`}
      data-testid={`vandaag-action-${action.action_id}`}
    >
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] group-hover:text-[#2E75B6] transition-colors truncate">
          {action.title}
        </h4>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-[#8B949E]">
            <MapPin className="w-3 h-3" />
            {locationMap[action.location_id] || action.location_id}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#30363D] text-slate-600 dark:text-[#C9D1D9] flex items-center gap-1">
            <TypeIcon className="w-2.5 h-2.5" />
            {typeLabel}
          </span>
          {action.event_type && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400 capitalize">
              {action.event_type.replace('_', ' ')}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className={`text-[10px] font-medium px-2 py-1 rounded-full whitespace-nowrap ${
          action.color === 'red' ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400' :
          action.color === 'amber' ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' :
          action.color === 'blue' ? 'bg-blue-50 text-[#2E75B6] dark:bg-blue-500/10 dark:text-[#58A6FF]' :
          'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400'
        }`}>
          {action.badge}
        </span>
        
        {action.type.startsWith('compliance') && (
          <button 
            onClick={handleCompleteCompliance}
            disabled={completing}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 hover:bg-green-100 text-slate-400 hover:text-green-600 dark:bg-[#30363D] dark:hover:bg-green-500/20 dark:text-[#8B949E] dark:hover:text-green-400 transition-colors flex-shrink-0"
            title="Afronden"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
        )}
        
        {action.type === 'entry_pending' && (
          <button 
            onClick={handleApproveEntry}
            disabled={completing}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 hover:border-[#2E75B6] text-xs text-slate-600 hover:text-[#2E75B6] dark:border-[#30363D] dark:text-[#8B949E] transition-colors"
          >
            {completing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Goedkeuren
          </button>
        )}
      </div>
    </button>
  );
}

// ---- Score Ring ----

function ScoreRing({ score, size = 32 }) {
  if (score == null) return null;
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 90 ? '#16A34A' : score >= 70 ? '#2E75B6' : score >= 50 ? '#EA580C' : '#DC2626';
  const bgColor = score >= 90 ? '#16A34A20' : score >= 70 ? '#2E75B620' : score >= 50 ? '#EA580C20' : '#DC262620';

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={bgColor} strokeWidth="2.5" />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth="2.5"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-bold tabular-nums"
        style={{ color, fontSize: size < 30 ? '8px' : '10px' }}
      >
        {Math.round(score)}
      </span>
    </div>
  );
}


// ---- Location Card ----

function LocationCard({ loc, stats, isPinned, onTogglePin, onNavigate, score, actions, large }) {
  const overdueCount = actions?.filter(a => a.location_id === loc.location_id && a.type === 'compliance_overdue').length || 0;
  const scoreTierBorder = score?.score != null
    ? score.score >= 90 ? 'border-l-green-500'
      : score.score >= 70 ? 'border-l-[#2E75B6]'
      : score.score >= 50 ? 'border-l-orange-500'
      : 'border-l-red-500'
    : '';

  return (
    <div
      className={`bg-white dark:bg-[#161B22] rounded-xl border text-left card-hover group relative ${
        isPinned
          ? 'border-amber-200 dark:border-amber-500/30 ring-1 ring-amber-100 dark:ring-amber-500/10'
          : 'border-slate-200/80 dark:border-[#21262D]'
      } ${score?.score != null ? `border-l-[3px] ${scoreTierBorder}` : ''} ${large ? 'p-4' : 'p-3'}`}
      data-testid={`dashboard-location-card-${loc.location_id}`}
    >
      {/* Pin button */}
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePin(loc.location_id); }}
        className={`absolute top-2.5 right-2.5 p-1.5 rounded-md transition-all duration-200 ${
          isPinned
            ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'
            : 'text-slate-300 hover:text-slate-500 hover:bg-slate-50 dark:text-[#484F58] dark:hover:text-[#8B949E] dark:hover:bg-[#1C2128] opacity-0 group-hover:opacity-100'
        }`}
        data-testid={`pin-location-${loc.location_id}`}
        title={isPinned ? 'Losmaken' : 'Vastzetten'}
      >
        {isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
      </button>

      <button className="w-full text-left" onClick={onNavigate}>
        <div className="flex items-center gap-3">
          {/* Score Ring */}
          <ScoreRing score={score?.score} size={large ? 36 : 28} />

          <div className="flex-1 min-w-0 pr-6">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className={`font-heading font-semibold text-slate-900 dark:text-[#E6EDF3] group-hover:text-[#2E75B6] transition-colors duration-200 truncate ${large ? 'text-sm' : 'text-xs'}`}>
                {loc.name}
              </h4>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_CLASSES[loc.status]}`}>
                {STATUS_LABELS[loc.status]}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-slate-400 dark:text-[#484F58] capitalize">{loc.bv_id}</span>
              {loc.bays && <span className="text-[10px] text-slate-400 dark:text-[#484F58]">{loc.bays} bays</span>}
              <div className="flex items-center gap-1.5 ml-auto">
                {overdueCount > 0 && (
                  <span className="text-[10px] font-medium text-red-600 dark:text-red-400 tabular-nums">{overdueCount} te laat</span>
                )}
                <span className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-[#484F58]">
                  <FileText className="w-3 h-3" />
                  <span className="tabular-nums">{stats?.by_location?.[loc.location_id] || 0}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}


// ---- KPI Card ----

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-3 sm:p-4 card-hover">
      <div className="flex items-center gap-2 mb-1 sm:mb-2">
        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
          <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color }} />
        </div>
        <span className="text-[11px] sm:text-xs text-slate-500 dark:text-[#8B949E]">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-heading font-bold text-slate-900 dark:text-[#E6EDF3] tabular-nums">{value}</p>
    </div>
  );
}


// ---- Insights Widget ----

const INSIGHT_TYPE_ICONS = {
  bundeling: { icon: Link2, color: '#2E75B6', bg: 'bg-blue-500/10' },
  missing: { icon: AlertTriangle, color: '#EA580C', bg: 'bg-orange-500/10' },
  risk: { icon: Flag, color: '#DC2626', bg: 'bg-red-500/10' },
  optimization: { icon: Zap, color: '#16A34A', bg: 'bg-green-500/10' },
};

function InsightsWidget({ insights, newCount, onUpdate, onViewAll }) {
  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="dashboard-insights-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
            <Lightbulb className="w-3.5 h-3.5 text-[#2E75B6]" />
          </div>
          <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">AI Inzichten</h3>
          {newCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#2E75B6]/10 text-[#2E75B6] font-semibold tabular-nums">
              {newCount} nieuw
            </span>
          )}
        </div>
        <button
          onClick={onViewAll}
          data-testid="insights-view-all-cta"
          className="flex items-center gap-1 text-xs text-[#2E75B6] hover:underline font-medium"
        >
          Bekijk alle inzichten
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {insights.slice(0, 5).map((ins) => {
          const cfg = INSIGHT_TYPE_ICONS[ins.type] || INSIGHT_TYPE_ICONS.optimization;
          const TypeIcon = cfg.icon;
          return (
            <div key={ins.insight_id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors">
              <div className={`w-7 h-7 rounded-md ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <TypeIcon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-900 dark:text-[#E6EDF3] leading-snug">{ins.title}</p>
                <p className="text-[10px] text-slate-400 dark:text-[#484F58] mt-0.5 line-clamp-1">{ins.description}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={(e) => { e.stopPropagation(); onUpdate(ins.insight_id, 'accepted'); }} className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400" title="Accepteren">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); onUpdate(ins.insight_id, 'dismissed'); }} className="p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-500/10 text-slate-400" title="Afwijzen">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Locatie Compliance Panel ----

function LocatieCompliancePanel({ scoresMap, locations, navigate }) {
  const locationScores = locations
    .filter(loc => loc.status === 'active' && scoresMap[loc.location_id])
    .map(loc => ({
      ...loc,
      score: scoresMap[loc.location_id].score || 0
    }))
    .sort((a, b) => a.score - b.score)
    .slice(0, 8);

  if (locationScores.length === 0) return null;

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="dashboard-compliance-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">Locatie Compliance</h3>
        <ShieldCheck className="w-4 h-4 text-slate-400" />
      </div>

      <div className="space-y-3">
        {locationScores.map(loc => {
          const score = loc.score;
          const colorClass = score >= 90 ? 'bg-green-500' : score >= 70 ? 'bg-[#2E75B6]' : score >= 50 ? 'bg-orange-500' : 'bg-red-500';
          const textClass = score >= 90 ? 'text-green-600' : score >= 70 ? 'text-[#2E75B6]' : score >= 50 ? 'text-orange-600' : 'text-red-600';
          
          return (
            <button
              key={loc.location_id}
              onClick={() => navigate(`/locatie/${loc.location_id}`)}
              className="w-full flex items-center justify-between group"
            >
              <div className="flex-1 min-w-0 mr-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[11px] font-medium text-slate-700 dark:text-[#E6EDF3] group-hover:text-[#2E75B6] transition-colors truncate text-left w-[85%]">
                    {loc.name}
                  </span>
                  <span className={`text-[10px] font-bold ${textClass} tabular-nums text-right w-[15%]`}>
                    {Math.round(score)}%
                  </span>
                </div>
                <div className="h-1.5 w-full bg-slate-100 dark:bg-[#21262D] rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
                    style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                  />
                </div>
              </div>
              <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}


function VertrouwelijkOverzicht({ authAxios, navigate }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const res = await api.get('/dashboard/visibility-stats');
        setStats(res.data);
      } catch (e) { /* owner-only, silent fail for non-owners */ }
    };
    load();
  }, [authAxios]);

  if (!stats || (stats.confidential === 0 && stats.restricted === 0)) return null;

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="vertrouwelijk-overzicht">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-4 h-4 text-slate-400" />
        <h3 className="font-heading text-sm font-semibold text-slate-700 dark:text-[#C9D1D9]">Vertrouwelijk overzicht</h3>
      </div>
      <div className="space-y-2">
        <button
          data-testid="visibility-confidential-link"
          onClick={() => navigate('/kennisbank?visibility=confidential')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-amber-50/50 dark:hover:bg-amber-500/5 transition-colors group"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-xs text-slate-600 dark:text-[#8B949E] group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">Vertrouwelijk</span>
          </div>
          <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 tabular-nums">{stats.confidential}</span>
        </button>
        <button
          data-testid="visibility-restricted-link"
          onClick={() => navigate('/kennisbank?visibility=restricted')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-red-50/50 dark:hover:bg-red-500/5 transition-colors group"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-xs text-slate-600 dark:text-[#8B949E] group-hover:text-red-700 dark:group-hover:text-red-400 transition-colors">Strikt vertrouwelijk</span>
          </div>
          <span className="text-sm font-semibold text-red-600 dark:text-red-400 tabular-nums">{stats.restricted}</span>
        </button>
      </div>
    </div>
  );
}
